/**
 * TimeInput24 - 24-hour time input component
 * Forces 24-hour format display regardless of browser locale
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Clock } from 'lucide-react';

export function TimeInput24({ 
  value, 
  onChange, 
  className = '',
  placeholder = 'HH:MM',
  ...props 
}) {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);

  // Parse time value to display in 24-hour format
  useEffect(() => {
    if (value) {
      // Value should be in HH:MM format
      setDisplayValue(value);
    }
  }, [value]);

  // Handle input changes
  const handleChange = (e) => {
    let input = e.target.value;
    
    // Remove non-numeric characters except colon
    input = input.replace(/[^\d:]/g, '');
    
    // Auto-insert colon
    if (input.length === 2 && !input.includes(':')) {
      input = input + ':';
    }
    
    // Limit to HH:MM format
    if (input.length > 5) {
      input = input.slice(0, 5);
    }
    
    setDisplayValue(input);
    
    // Validate and emit
    if (/^([01]?[0-9]|2[0-3]):([0-5][0-9])$/.test(input)) {
      // Pad hours if needed
      const parts = input.split(':');
      const paddedValue = parts[0].padStart(2, '0') + ':' + parts[1];
      onChange?.(paddedValue);
    }
  };

  // Handle blur - validate and format
  const handleBlur = () => {
    if (displayValue) {
      const match = displayValue.match(/^(\d{1,2}):?(\d{0,2})$/);
      if (match) {
        let hours = parseInt(match[1], 10);
        let minutes = parseInt(match[2] || '0', 10);
        
        // Clamp values
        hours = Math.min(23, Math.max(0, hours));
        minutes = Math.min(59, Math.max(0, minutes));
        
        const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        setDisplayValue(formatted);
        onChange?.(formatted);
      }
    }
  };

  // Quick time presets
  const presets = [
    { label: '06:00', value: '06:00' },
    { label: '08:00', value: '08:00' },
    { label: '12:00', value: '12:00' },
    { label: '18:00', value: '18:00' },
    { label: '22:00', value: '22:00' },
    { label: '00:00', value: '00:00' },
  ];

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`pl-9 font-mono ${className}`}
          maxLength={5}
          {...props}
        />
        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
      </div>
      
      {/* Quick presets on focus */}
      <div className="absolute top-full left-0 right-0 mt-1 hidden group-focus-within:flex flex-wrap gap-1 p-2 bg-[#18181b] border border-[#3f3f46] rounded-lg z-50">
        {presets.map((preset) => (
          <button
            key={preset.value}
            type="button"
            className="px-2 py-1 text-xs font-mono bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded"
            onClick={() => {
              setDisplayValue(preset.value);
              onChange?.(preset.value);
            }}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default TimeInput24;
